import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  GpsFixedRounded as TargetIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
} from '@mui/icons-material';
import { useTheme } from '../../context/ThemeContext';
import { SortOption, TouchPoint, TouchPointHoverPreview, TripHoverPreview, ViewportOcclusionRect } from '../../models/types';
import CompactSearchSortControl, { CompactSortOption } from '../common/CompactSearchSortControl';
import FloatingWorkspaceCard from '../workspace/FloatingWorkspaceCard';
import {
  TimelineBounds,
  TimelineMarker,
  TimelineStripData,
  formatTimelineTick,
  getTimelineTicks,
  projectTimelineMinute,
} from './timelineUtils';

const LABEL_WIDTH = 192;
const ROW_HEIGHT = 62;
const TRACK_TOP = 32;
const TRACK_HEIGHT = 4;
const MARKER_SIZE = 22;
const PANEL_PADDING_X = 20;
const PANEL_MAX_WIDTH = 1380;
const PANEL_MIN_WIDTH = 520;
const PANEL_MAX_HEIGHT = 640;
const PANEL_MIN_HEIGHT = 180;
const PANEL_VIEWPORT_HEIGHT_RATIO = 0.76;
const PANEL_VIEWPORT_MARGIN = 24;
const PANEL_CARD_CHROME_HEIGHT = 134;
const PANEL_ROW_GAP = 4.5;

interface TimelinePanelLayout {
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  defaultHeight: number;
  minHeight: number;
  maxHeight: number;
}

export const getTimelinePanelLayout = (
  contentWidth: number,
  stripCount: number,
  positionX: number,
  viewport: { width: number; height: number }
): TimelinePanelLayout => {
  const naturalWidth = Math.min(PANEL_MAX_WIDTH, contentWidth + LABEL_WIDTH + PANEL_PADDING_X);
  const availableWidth = Math.max(1, viewport.width - positionX - PANEL_VIEWPORT_MARGIN);
  const maxWidth = Math.min(naturalWidth, availableWidth);
  const minWidth = Math.min(PANEL_MIN_WIDTH, maxWidth);
  const maxHeight = Math.min(
    PANEL_MAX_HEIGHT,
    Math.max(PANEL_MIN_HEIGHT, Math.floor(viewport.height * PANEL_VIEWPORT_HEIGHT_RATIO))
  );
  const contentHeight = Math.ceil(PANEL_CARD_CHROME_HEIGHT + (Math.max(1, stripCount) * (ROW_HEIGHT + PANEL_ROW_GAP)));
  const defaultHeight = Math.min(maxHeight, contentHeight);

  return {
    defaultWidth: maxWidth,
    minWidth,
    maxWidth,
    defaultHeight,
    minHeight: Math.min(PANEL_MIN_HEIGHT, defaultHeight),
    maxHeight,
  };
};

const COLORS = {
  leadIn: '#0f766e',
  commute: '#2563eb',
  service: '#7c3aed',
  tail: '#b45309',
};

interface TimelineWorkspaceProps {
  strips: TimelineStripData[];
  bounds: TimelineBounds;
  position: { x: number; y: number };
  width?: number;
  height?: number;
  mode: 'manual' | 'table';
  onPositionChange: (position: { x: number; y: number }) => void;
  onWidthChange?: (width: number) => void;
  onHeightChange?: (height: number) => void;
  onCloseTrip: (tripName: string) => void;
  onCloseAll: () => void;
  onReorderTrip: (tripName: string, targetIndex: number) => void;
  onFocusTrip?: (tripName: string) => void;
  onOcclusionRectChange?: (rect: ViewportOcclusionRect | null) => void;
  searchTerm: string;
  sortConfig: {
    key: SortOption;
    direction: 'asc' | 'desc';
  };
  onSearch: (term: string) => void;
  onSortKeyChange: (key: SortOption) => void;
  onSortDirectionChange: (direction: 'asc' | 'desc') => void;
  onHoverTouchPoint?: (preview: TouchPointHoverPreview | null) => void;
  onHoverTrip?: (preview: TripHoverPreview | null) => void;
  onFocusTouchPoint?: (touchPoint: TouchPoint) => void;
}

interface TimelineAxisProps {
  bounds: TimelineBounds;
  scrollLeft: number;
  onScrollLeftChange: (left: number) => void;
  viewportWidth: number;
  onViewportWidthChange: (width: number) => void;
  onCloseAll: () => void;
  searchTerm: string;
  sortConfig: {
    key: SortOption;
    direction: 'asc' | 'desc';
  };
  onSearch: (term: string) => void;
  onSortKeyChange: (key: SortOption) => void;
  onSortDirectionChange: (direction: 'asc' | 'desc') => void;
}

interface TimelineStripProps {
  data: TimelineStripData;
  bounds: TimelineBounds;
  scrollLeft: number;
  onScrollLeftChange: (left: number) => void;
  dragging?: boolean;
  onHoverTouchPoint?: (preview: TouchPointHoverPreview | null) => void;
  onHoverTrip?: (preview: TripHoverPreview | null) => void;
  onFocusTouchPoint?: (touchPoint: TouchPoint) => void;
  onFocusTrip?: () => void;
  onClose?: () => void;
  onRowDragPointerDown?: (event: React.PointerEvent<HTMLButtonElement>) => void;
}

const useSyncedScroll = (
  scrollLeft: number,
  onScrollLeftChange: (left: number) => void,
  onViewportWidthChange?: (width: number) => void
) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const syncLockRef = useRef(false);

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node || Math.abs(node.scrollLeft - scrollLeft) < 1) {
      return;
    }
    syncLockRef.current = true;
    node.scrollLeft = scrollLeft;
    window.requestAnimationFrame(() => {
      syncLockRef.current = false;
    });
  }, [scrollLeft]);

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node || !onViewportWidthChange) {
      return;
    }

    const publishWidth = () => onViewportWidthChange(node.clientWidth);
    publishWidth();

    const observer = new ResizeObserver(() => {
      publishWidth();
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [onViewportWidthChange]);

  const handleScroll = () => {
    if (syncLockRef.current || !scrollerRef.current) {
      return;
    }
    onScrollLeftChange(scrollerRef.current.scrollLeft);
  };

  return { scrollerRef, handleScroll };
};

const ScrollViewport: React.FC<{
  scrollLeft: number;
  onScrollLeftChange: (left: number) => void;
  onViewportWidthChange?: (width: number) => void;
  showScrollbar?: boolean;
  children: React.ReactNode;
}> = ({ scrollLeft, onScrollLeftChange, onViewportWidthChange, showScrollbar = true, children }) => {
  const { scrollerRef, handleScroll } = useSyncedScroll(scrollLeft, onScrollLeftChange, onViewportWidthChange);

  return (
    <Box
      ref={scrollerRef}
      onScroll={handleScroll}
      onWheel={(event) => event.stopPropagation()}
      sx={{
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        pointerEvents: 'auto',
        '&::-webkit-scrollbar': {
          height: showScrollbar ? 8 : 0,
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: 999,
          backgroundColor: showScrollbar ? 'rgba(148,163,184,0.55)' : 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        scrollbarWidth: showScrollbar ? 'thin' : 'none',
      }}
    >
      {children}
    </Box>
  );
};

const formatTimeLabel = (minutes: number): string => {
  const normalized = ((minutes % 1440) + 1440) % 1440;
  const hours24 = Math.floor(normalized / 60);
  const mins = normalized % 60;
  const suffix = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${String(mins).padStart(2, '0')} ${suffix}`;
};

const getAnchorClientPosition = (target: EventTarget | null): { x: number; y: number } | null => {
  if (!(target instanceof Element)) {
    return null;
  }
  const rect = target.getBoundingClientRect();
  return {
    x: rect.left + (rect.width / 2),
    y: rect.top + (rect.height / 2),
  };
};

const clampNumber = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getMarkerTextColor = (backgroundColor: string): string => {
  const normalized = backgroundColor.replace('#', '');
  if (normalized.length !== 6) {
    return '#ffffff';
  }
  const parsed = Number.parseInt(normalized, 16);
  const red = (parsed >> 16) & 255;
  const green = (parsed >> 8) & 255;
  const blue = parsed & 255;
  const luminance = ((0.299 * red) + (0.587 * green) + (0.114 * blue)) / 255;
  return luminance > 0.62 ? '#0f172a' : '#ffffff';
};

const getHourlyGuideMinutes = (bounds: TimelineBounds): number[] => {
  const firstHour = Math.ceil(bounds.start / 60) * 60;
  const guides: number[] = [];
  for (let minute = firstHour; minute <= bounds.end; minute += 60) {
    guides.push(minute);
  }
  return guides;
};

const TimelineAxis: React.FC<TimelineAxisProps> = ({
  bounds,
  scrollLeft,
  onScrollLeftChange,
  viewportWidth,
  onViewportWidthChange,
  onCloseAll,
  searchTerm,
  sortConfig,
  onSearch,
  onSortKeyChange,
  onSortDirectionChange,
}) => {
  const { mode: themeMode } = useTheme();
  const ticks = useMemo(() => getTimelineTicks(bounds.start, bounds.end), [bounds.end, bounds.start]);
  const hourlyGuides = useMemo(() => getHourlyGuideMinutes(bounds), [bounds]);
  const maxScroll = Math.max(0, bounds.contentWidth - viewportWidth);
  const sortOptions: CompactSortOption<SortOption>[] = [
    { value: 'tripId', label: 'Trip ID' },
    { value: 'distance', label: 'Distance' },
    { value: 'tpCount', label: 'Touch Points' },
    { value: 'weight', label: 'Weight' },
    { value: 'duration', label: 'Duration' },
  ];

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 3,
        display: 'grid',
        gridTemplateColumns: `${LABEL_WIDTH}px minmax(0, 1fr)`,
        gap: 1,
        alignItems: 'stretch',
        px: 0.1,
        pt: 0.1,
        pb: 0.45,
        pointerEvents: 'auto',
        background: themeMode === 'dark'
          ? 'linear-gradient(180deg, rgba(2,6,23,0.96), rgba(2,6,23,0.78))'
          : 'linear-gradient(180deg, rgba(248,250,252,0.96), rgba(248,250,252,0.78))',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Stack spacing={0.45} data-timeline-no-drag="true">
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', minHeight: 28 }}>
          <Tooltip title="Close timelines">
            <IconButton
              size="small"
              onClick={onCloseAll}
              aria-label="Close timelines"
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.2,
                color: themeMode === 'dark' ? '#e2e8f0' : '#0f172a',
                backgroundColor: themeMode === 'dark' ? 'rgba(15,23,42,0.82)' : 'rgba(255,255,255,0.84)',
                border: '1px solid',
                borderColor: themeMode === 'dark' ? 'rgba(148,163,184,0.18)' : 'rgba(15,23,42,0.10)',
                '&:hover': {
                  backgroundColor: themeMode === 'dark' ? 'rgba(15,23,42,0.96)' : 'rgba(255,255,255,0.98)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <CompactSearchSortControl
          query={searchTerm}
          onQueryChange={onSearch}
          placeholder="Search trips"
          sortKey={sortConfig.key}
          sortDirection={sortConfig.direction}
          sortOptions={sortOptions}
          onSortKeyChange={onSortKeyChange}
          onSortDirectionChange={onSortDirectionChange}
          sx={{
            gap: 0.45,
            '& .MuiOutlinedInput-root': {
              minHeight: 28,
              borderRadius: 1.2,
              fontSize: '0.78rem',
            },
            '& .MuiInputBase-input': {
              py: '5px',
            },
            '& .MuiIconButton-root': {
              width: 28,
              height: 28,
              borderRadius: 1.2,
            },
          }}
        />
      </Stack>

      <Box sx={{ minWidth: 0, alignSelf: 'end' }}>
        <ScrollViewport
          scrollLeft={scrollLeft}
          onScrollLeftChange={onScrollLeftChange}
          onViewportWidthChange={onViewportWidthChange}
          showScrollbar={false}
        >
          <Box
            sx={{
              width: bounds.contentWidth,
              minWidth: bounds.contentWidth,
              position: 'relative',
              height: 30,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 24,
                height: 1,
                backgroundColor: themeMode === 'dark'
                  ? 'rgba(148,163,184,0.22)'
                  : 'rgba(15,23,42,0.12)',
              }}
            />
            {hourlyGuides.map((minute) => (
              <Box
                key={`axis-hour-${minute}`}
                sx={{
                  position: 'absolute',
                  left: projectTimelineMinute(minute, bounds),
                  top: 0,
                  bottom: 0,
                  borderLeft: '1.5px dotted',
                  borderColor: themeMode === 'dark'
                    ? 'rgba(148,163,184,0.34)'
                    : 'rgba(15,23,42,0.26)',
                  pointerEvents: 'none',
                }}
              />
            ))}
            {ticks.map((minute) => {
              const left = projectTimelineMinute(minute, bounds);
              const labelTransform = left <= 34
                ? 'translateX(0)'
                : left >= bounds.contentWidth - 34
                  ? 'translateX(-100%)'
                  : 'translateX(-50%)';
              return (
                <Box key={minute} sx={{ position: 'absolute', left, top: 0 }}>
                  <Typography
                    sx={{
                      transform: labelTransform,
                      fontSize: 10.5,
                      fontWeight: 800,
                      letterSpacing: 0.3,
                      color: themeMode === 'dark' ? '#e2e8f0' : '#0f172a',
                      fontFamily: '"IBM Plex Mono", "SFMono-Regular", Consolas, monospace',
                    }}
                  >
                    {formatTimelineTick(minute)}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.15,
                      transform: 'translateX(-50%)',
                      width: 1,
                      height: 12,
                      backgroundColor: themeMode === 'dark'
                        ? 'rgba(148,163,184,0.24)'
                        : 'rgba(15,23,42,0.14)',
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </ScrollViewport>
        {maxScroll > 0 && (
          <Box sx={{ px: 0.35, mt: 0.2, pointerEvents: 'auto' }}>
            <Slider
              size="small"
              min={0}
              max={maxScroll}
              value={Math.min(scrollLeft, maxScroll)}
              onChange={(_, value) => onScrollLeftChange(Array.isArray(value) ? value[0] : value)}
              sx={{
                py: 0,
                color: themeMode === 'dark' ? '#94a3b8' : '#64748b',
                '& .MuiSlider-rail': {
                  opacity: 1,
                  height: 4,
                  backgroundColor: themeMode === 'dark'
                    ? 'rgba(15,23,42,0.54)'
                    : 'rgba(148,163,184,0.26)',
                },
                '& .MuiSlider-track': {
                  height: 4,
                  border: 'none',
                },
                '& .MuiSlider-thumb': {
                  width: 16,
                  height: 16,
                  backgroundColor: themeMode === 'dark' ? '#e2e8f0' : '#0f172a',
                  boxShadow: themeMode === 'dark'
                    ? '0 2px 8px rgba(2,6,23,0.42)'
                    : '0 2px 8px rgba(15,23,42,0.24)',
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const TimelineStrip: React.FC<TimelineStripProps> = ({
  data,
  bounds,
  scrollLeft,
  onScrollLeftChange,
  dragging = false,
  onHoverTouchPoint,
  onHoverTrip,
  onFocusTouchPoint,
  onFocusTrip,
  onClose,
  onRowDragPointerDown,
}) => {
  const { mode: themeMode } = useTheme();
  const stops = data.markers.filter((marker) => marker.kind === 'stop');
  const hourlyGuides = useMemo(() => getHourlyGuideMinutes(bounds), [bounds]);
  const firstStop = stops[0];
  const firstStopOverlapsDepotStart = firstStop
    ? Math.abs(projectTimelineMinute(firstStop.minute, bounds) - projectTimelineMinute(data.tripStart, bounds)) <= MARKER_SIZE + 4
    : false;
  const getMarkerLeft = (marker: TimelineMarker) => {
    const baseLeft = projectTimelineMinute(marker.minute, bounds);
    const clampedLeft = clampNumber(baseLeft, MARKER_SIZE / 2, bounds.contentWidth - (MARKER_SIZE / 2));

    if (firstStopOverlapsDepotStart && marker.kind === 'stop' && marker.id === firstStop?.id) {
      return clampNumber(clampedLeft + MARKER_SIZE + 6, MARKER_SIZE / 2, bounds.contentWidth - (MARKER_SIZE / 2));
    }

    return clampedLeft;
  };

  const serviceSegments = stops
    .filter((marker) => marker.serviceEnd > marker.serviceStart)
    .map((marker) => ({
      id: `${marker.id}::service`,
      start: marker.serviceStart,
      end: marker.serviceEnd,
      color: COLORS.service,
      label: `Service ${formatTimeLabel(marker.serviceStart)} - ${formatTimeLabel(marker.serviceEnd)}`,
    }));

  const commuteSegments: Array<{ id: string; start: number; end: number; color: string; label: string }> = [];
  let cursor = data.tripStart;
  stops.forEach((marker) => {
    if (marker.serviceStart > cursor) {
      commuteSegments.push({
        id: `${marker.id}::commute-before`,
        start: cursor,
        end: marker.serviceStart,
        color: COLORS.commute,
        label: `Commute ${formatTimeLabel(cursor)} - ${formatTimeLabel(marker.serviceStart)}`,
      });
    }
    cursor = Math.max(cursor, marker.serviceEnd);
  });
  if (data.tripEnd > cursor) {
    commuteSegments.push({
      id: `${data.tripName}::return`,
      start: cursor,
      end: data.tripEnd,
      color: COLORS.commute,
      label: `Return ${formatTimeLabel(cursor)} - ${formatTimeLabel(data.tripEnd)}`,
    });
  }

  const operatingSegments = [
    data.shiftStart != null && data.shiftStart < data.tripStart
      ? {
          id: `${data.tripName}::lead-in`,
          start: data.shiftStart,
          end: data.tripStart,
          color: COLORS.leadIn,
          label: `Operating start ${formatTimeLabel(data.shiftStart)} - ${formatTimeLabel(data.tripStart)}`,
        }
      : null,
    data.shiftEnd != null && data.shiftEnd > data.tripEnd
      ? {
          id: `${data.tripName}::tail`,
          start: data.tripEnd,
          end: data.shiftEnd,
          color: COLORS.tail,
          label: `Operating end ${formatTimeLabel(data.tripEnd)} - ${formatTimeLabel(data.shiftEnd)}`,
        }
      : null,
  ].filter((segment): segment is NonNullable<typeof segment> => !!segment);

  const renderSegment = (
    id: string,
    start: number,
    end: number,
    color: string,
    label: string,
    top: number,
    height: number,
    dashed = false
  ) => {
    if (end <= start) {
      return null;
    }

    const left = projectTimelineMinute(start, bounds);
    const width = Math.max(3, projectTimelineMinute(end, bounds) - left);

    return (
      <Tooltip key={id} title={label} arrow>
        <Box
          data-timeline-no-drag="true"
          sx={{
            position: 'absolute',
            left,
            top,
            width,
            height,
            borderRadius: 999,
            backgroundColor: color,
            opacity: 0.95,
            boxShadow: `0 0 0 1px ${color}22`,
            pointerEvents: 'auto',
            ...(dashed
              ? {
                  backgroundImage: `linear-gradient(90deg, ${color} 60%, transparent 0%)`,
                  backgroundSize: '8px 100%',
                  backgroundColor: 'transparent',
                }
              : null),
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `${LABEL_WIDTH}px minmax(0, 1fr)`,
        gap: 1,
        alignItems: 'center',
        minHeight: ROW_HEIGHT,
        pointerEvents: 'none',
        opacity: dragging ? 0.92 : 1,
      }}
    >
      <Box
        onMouseEnter={(event) =>
          onHoverTrip?.({
            tripName: data.tripName,
            anchorClientPosition: {
              x: event.clientX,
              y: event.clientY,
            },
            source: 'timeline',
          })
        }
        onMouseMove={(event) =>
          onHoverTrip?.({
            tripName: data.tripName,
            anchorClientPosition: {
              x: event.clientX,
              y: event.clientY,
            },
            source: 'timeline',
          })
        }
        onMouseLeave={() => onHoverTrip?.(null)}
        onFocus={(event) => {
          const anchorClientPosition = getAnchorClientPosition(event.currentTarget);
          if (!anchorClientPosition) {
            return;
          }
          onHoverTrip?.({
            tripName: data.tripName,
            anchorClientPosition,
            source: 'timeline',
          });
        }}
        onBlur={() => onHoverTrip?.(null)}
        sx={{
          px: 1,
          py: 0.35,
          borderRadius: 1.5,
          backgroundColor: themeMode === 'dark' ? 'rgba(2,6,23,0.72)' : 'rgba(255,255,255,0.72)',
          border: '1px solid',
          borderColor: themeMode === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(15,23,42,0.08)',
          pointerEvents: 'auto',
          position: 'relative',
          overflow: 'hidden',
          transition: 'border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            backgroundColor: data.tripColor,
          },
          '&:hover, &:focus-within': {
            borderColor: data.tripColor,
            backgroundColor: themeMode === 'dark' ? 'rgba(15,23,42,0.88)' : 'rgba(255,255,255,0.92)',
            boxShadow: `0 10px 20px ${data.tripColor}22`,
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.5}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 13.25,
                fontWeight: 600,
                color: themeMode === 'dark' ? '#f8fafc' : '#0f172a',
                letterSpacing: 0,
                lineHeight: 1.08,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {data.tripName}
            </Typography>
            <Typography
              sx={{
                fontSize: 10.5,
                color: themeMode === 'dark' ? 'rgba(226,232,240,0.72)' : 'rgba(15,23,42,0.62)',
                fontFamily: '"IBM Plex Mono", "SFMono-Regular", Consolas, monospace',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {data.vehicleLabel}
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" spacing={0.15}>
            {onFocusTrip && (
              <Tooltip title="Focus trip on map">
                <IconButton
                  size="small"
                  onClick={onFocusTrip}
                  aria-label="Focus trip on map"
                  sx={{ color: themeMode === 'dark' ? '#e2e8f0' : '#0f172a' }}
                >
                  <TargetIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
            {onRowDragPointerDown && (
              <Tooltip title="Reorder strip">
                <IconButton
                  size="small"
                  onPointerDown={onRowDragPointerDown}
                  sx={{ color: themeMode === 'dark' ? '#e2e8f0' : '#0f172a' }}
                >
                  <DragIndicatorIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onClose && (
              <Tooltip title="Close strip">
                <IconButton
                  size="small"
                  onClick={onClose}
                  sx={{ color: themeMode === 'dark' ? '#e2e8f0' : '#0f172a' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Box>

      <ScrollViewport scrollLeft={scrollLeft} onScrollLeftChange={onScrollLeftChange}>
        <Box
          sx={{
            width: bounds.contentWidth,
            minWidth: bounds.contentWidth,
            height: 52,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: TRACK_TOP + 1,
              height: 1,
              backgroundColor: themeMode === 'dark'
                ? 'rgba(148,163,184,0.14)'
                : 'rgba(15,23,42,0.10)',
            }}
          />
          {hourlyGuides.map((minute) => (
            <Box
              key={`row-hour-${data.tripName}-${minute}`}
              sx={{
                position: 'absolute',
                left: projectTimelineMinute(minute, bounds),
                top: 0,
                bottom: 0,
                borderLeft: '1.5px dotted',
                borderColor: themeMode === 'dark'
                  ? 'rgba(148,163,184,0.30)'
                  : 'rgba(15,23,42,0.24)',
                pointerEvents: 'none',
              }}
            />
          ))}

          {operatingSegments.map((segment) =>
            renderSegment(segment.id, segment.start, segment.end, segment.color, segment.label, TRACK_TOP + 1, 3, true)
          )}
          {commuteSegments.map((segment) =>
            renderSegment(segment.id, segment.start, segment.end, segment.color, segment.label, TRACK_TOP, TRACK_HEIGHT)
          )}
          {serviceSegments.map((segment) =>
            renderSegment(segment.id, segment.start, segment.end, segment.color, segment.label, TRACK_TOP - 1, 6)
          )}

          <Tooltip title={`Trip ${formatTimeLabel(data.tripStart)} - ${formatTimeLabel(data.tripEnd)}`} arrow>
            <Box
              data-timeline-no-drag="true"
              sx={{
                position: 'absolute',
                left: projectTimelineMinute(data.tripStart, bounds),
                top: 0,
                transform: 'translateX(-50%)',
                px: 0.6,
                py: 0.15,
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 800,
                color: '#ffffff',
                backgroundColor: COLORS.commute,
                fontFamily: '"IBM Plex Mono", "SFMono-Regular", Consolas, monospace',
                pointerEvents: 'auto',
              }}
            >
              {formatTimeLabel(data.tripStart)}
            </Box>
          </Tooltip>

          <Tooltip title={`Trip end ${formatTimeLabel(data.tripEnd)}`} arrow>
            <Box
              data-timeline-no-drag="true"
              sx={{
                position: 'absolute',
                left: projectTimelineMinute(data.tripEnd, bounds),
                top: 0,
                transform: 'translateX(-50%)',
                px: 0.6,
                py: 0.15,
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 800,
                color: '#ffffff',
                backgroundColor: COLORS.service,
                fontFamily: '"IBM Plex Mono", "SFMono-Regular", Consolas, monospace',
                pointerEvents: 'auto',
              }}
            >
              {formatTimeLabel(data.tripEnd)}
            </Box>
          </Tooltip>

          {data.markers.map((marker) => {
            const left = getMarkerLeft(marker);
            const isDepot = marker.kind !== 'stop';
            const markerColor = marker.color || data.tripColor;
            const markerTitle = marker.kind === 'depot-start'
              ? `Depot start ${formatTimeLabel(marker.minute)}`
              : marker.kind === 'depot-end'
                ? `Depot end ${formatTimeLabel(marker.minute)}`
                : `${marker.label} ${formatTimeLabel(marker.minute)}`;

            return (
              <Tooltip key={marker.id} title={markerTitle} arrow>
                <Box
                  data-timeline-no-drag="true"
                  onMouseEnter={(event) =>
                    marker.touchPoint && onHoverTouchPoint?.({
                      touchPoint: marker.touchPoint,
                      anchorClientPosition: {
                        x: event.clientX,
                        y: event.clientY,
                      },
                      source: 'timeline',
                    })
                  }
                  onMouseLeave={() => marker.touchPoint && onHoverTouchPoint?.(null)}
                  onFocus={(event) => {
                    if (!marker.touchPoint) {
                      return;
                    }
                    const anchorClientPosition = getAnchorClientPosition(event.currentTarget);
                    if (!anchorClientPosition) {
                      return;
                    }
                    onHoverTouchPoint?.({
                      touchPoint: marker.touchPoint,
                      anchorClientPosition,
                      source: 'timeline',
                    });
                  }}
                  onBlur={() => marker.touchPoint && onHoverTouchPoint?.(null)}
                  onClick={() => marker.touchPoint && onFocusTouchPoint?.(marker.touchPoint)}
                  sx={{
                    position: 'absolute',
                    left,
                    top: TRACK_TOP - ((MARKER_SIZE - TRACK_HEIGHT) / 2),
                    transform: 'translateX(-50%)',
                    width: MARKER_SIZE,
                    height: MARKER_SIZE,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: isDepot ? 8.5 : 10,
                    fontWeight: 900,
                    fontFamily: '"IBM Plex Mono", "SFMono-Regular", Consolas, monospace',
                    color: isDepot ? data.tripColor : getMarkerTextColor(markerColor),
                    backgroundColor: isDepot
                      ? (themeMode === 'dark' ? '#f8fafc' : '#ffffff')
                      : markerColor,
                    border: `2px solid ${isDepot ? data.tripColor : (themeMode === 'dark' ? '#0f172a' : '#ffffff')}`,
                    boxShadow: isDepot
                      ? `0 0 0 4px ${data.tripColor}22`
                      : `0 0 0 4px ${markerColor}1f`,
                    cursor: marker.touchPoint ? 'pointer' : 'default',
                    pointerEvents: 'auto',
                    zIndex: isDepot ? 2 : 3,
                  }}
                >
                  {marker.shortLabel}
                  {marker.hasTimeBreach && (
                    <WarningAmberRoundedIcon
                      sx={{
                        position: 'absolute',
                        top: -11,
                        right: -7,
                        fontSize: 14,
                        color: '#f59e0b',
                        filter: 'drop-shadow(0 1px 2px rgba(15,23,42,0.35))',
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </ScrollViewport>
    </Box>
  );
};

export const TimelineWorkspace: React.FC<TimelineWorkspaceProps> = ({
  strips,
  bounds,
  position,
  width,
  height,
  mode,
  onPositionChange,
  onWidthChange,
  onHeightChange,
  onCloseTrip,
  onCloseAll,
  onReorderTrip,
  onFocusTrip,
  onOcclusionRectChange,
  searchTerm,
  sortConfig,
  onSearch,
  onSortKeyChange,
  onSortDirectionChange,
  onHoverTouchPoint,
  onHoverTrip,
  onFocusTouchPoint,
}) => {
  const [scrollLeft, setScrollLeft] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window === 'undefined' ? 1440 : window.innerWidth,
    height: typeof window === 'undefined' ? 900 : window.innerHeight,
  }));
  const [draggingTripName, setDraggingTripName] = useState<string | null>(null);
  const panelLayout = getTimelinePanelLayout(
    bounds.contentWidth,
    strips.length,
    position.x,
    viewportSize
  );
  const rowDragRef = useRef<{
    pointerId: number;
    tripName: string;
    index: number;
    startY: number;
  } | null>(null);

  useEffect(() => {
    setScrollLeft(0);
  }, [bounds.contentWidth, bounds.end, bounds.start, strips.length]);

  useEffect(() => {
    const handleViewportResize = () => setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleViewportResize);
    return () => window.removeEventListener('resize', handleViewportResize);
  }, []);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      if (rowDragRef.current?.pointerId === event.pointerId) {
        const deltaY = event.clientY - rowDragRef.current.startY;
        const targetIndex = Math.max(
          0,
          Math.min(
            strips.length - 1,
            Math.round((rowDragRef.current.index * ROW_HEIGHT + deltaY) / ROW_HEIGHT)
          )
        );
        if (targetIndex !== rowDragRef.current.index) {
          onReorderTrip(rowDragRef.current.tripName, targetIndex);
          rowDragRef.current.index = targetIndex;
          rowDragRef.current.startY = event.clientY;
        }
      }
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (rowDragRef.current?.pointerId === event.pointerId) {
        rowDragRef.current = null;
        setDraggingTripName(null);
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [onReorderTrip, strips.length]);

  if (!strips.length) {
    return null;
  }

  return (
    <FloatingWorkspaceCard
      title="Trip timelines"
      subtitle={`${strips.length} ${strips.length === 1 ? 'trip' : 'trips'} · ${mode === 'table' ? 'table order' : 'manual order'}`}
      position={position}
      defaultPosition={position}
      size={{ width: width ?? panelLayout.defaultWidth, height: height ?? panelLayout.defaultHeight }}
      defaultSize={{ width: panelLayout.defaultWidth, height: panelLayout.defaultHeight }}
      minSize={{ width: panelLayout.minWidth, height: panelLayout.minHeight }}
      maxSize={{ width: panelLayout.maxWidth, height: panelLayout.maxHeight }}
      onPositionChange={onPositionChange}
      onSizeChange={(nextSize) => {
        onWidthChange?.(nextSize.width);
        onHeightChange?.(nextSize.height);
      }}
      onClose={onCloseAll}
      onOcclusionRectChange={onOcclusionRectChange}
      dragSurface="card"
      accentColor="#0e7490"
      contentSx={{ p: 1.1 }}
      testId="timeline-workspace-card"
    >
      <Stack
        spacing={0.55}
        sx={{
          minWidth: 0,
          minHeight: 0,
          height: '100%',
          flex: 1,
          overflowY: 'auto',
          pr: 0.3,
          pb: 1,
          scrollPaddingBottom: 12,
          boxSizing: 'border-box',
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: 999,
            backgroundColor: 'rgba(148,163,184,0.55)',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          scrollbarWidth: 'thin',
        }}
      >
        <TimelineAxis
          bounds={bounds}
          scrollLeft={scrollLeft}
          onScrollLeftChange={setScrollLeft}
          viewportWidth={viewportWidth}
          onViewportWidthChange={setViewportWidth}
          onCloseAll={onCloseAll}
          searchTerm={searchTerm}
          sortConfig={sortConfig}
          onSearch={onSearch}
          onSortKeyChange={onSortKeyChange}
          onSortDirectionChange={onSortDirectionChange}
        />

        {strips.map((strip, index) => (
          <TimelineStrip
            key={strip.tripName}
            data={strip}
            bounds={bounds}
            scrollLeft={scrollLeft}
            onScrollLeftChange={setScrollLeft}
            dragging={draggingTripName === strip.tripName}
            onHoverTouchPoint={onHoverTouchPoint}
            onHoverTrip={onHoverTrip}
            onFocusTouchPoint={onFocusTouchPoint}
            onFocusTrip={onFocusTrip ? () => onFocusTrip(strip.tripName) : undefined}
            onClose={() => onCloseTrip(strip.tripName)}
            onRowDragPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setDraggingTripName(strip.tripName);
              rowDragRef.current = {
                pointerId: event.pointerId,
                tripName: strip.tripName,
                index,
                startY: event.clientY,
              };
            }}
          />
        ))}
      </Stack>
    </FloatingWorkspaceCard>
  );
};
